import Vue from 'vue';
import Vuex, { Store } from 'vuex';
import { Breakpoint } from 'vuetify/types/services/breakpoint.d';
import {
  AlertMessage,
  FirebaseLesson,
  Lang,
  Lesson,
  Level,
  PersonalizedCourse,
  Phrase,
  Skill,
  StringMap,
  Unit,
  User,
  LastVisitedPhraseForSupport,
} from '@/store/types';
import api from './api';

Vue.use(Vuex);

const debugMode = process.env.NODE_ENV !== 'production';

export default new Vuex.Store({
  strict: debugMode,
  state: {
    debugMode,
    mainLoading: true,
    windowWidth: 0,
    windowHeight: 0,
    sleepPromise: null as Promise<void> | null,
    globalVolume: 1,
    osName: '',
    breakpoint: {} as Breakpoint,
    alertMessageText: '',
    usernamePopup: false,
    mobileLinksPopup: false,
    developmentPopup: false,
    tr: {},
    translationsDefault: {},
    rtlLangs: ['7', '8', '40', '51', '52', '95'],
    languagesBluebird: Array<string>(),
    videoLanguages: new Map<string, Array<string>>(),
    langsI: Array<Lang>(),
    langsL: Array<Lang>(),
    langI: null as Lang | null,
    langL: null as Lang | null,
    skills: Array<Skill>(),
    firebaseToken: '',
    user: null as User | null,
    validInstitutePin: false,
    userLessons: Array<FirebaseLesson>(),
    levelsAllowed: Array<string>(),
    postcardsImages: new Map<string, Array<string>>(),
    levels: Array<Level>(),
    level: null as Level | null,
    levelsPersonalized: null as Level | null,
    levelDaily: null as Level | null,
    units: Array<Unit>(),
    unit: null as Unit | null,
    lessons: Array<Lesson>(),
    lesson: null as Lesson | null,
    phrases: Array<Phrase>(),
    flashcardsStatus: {} as StringMap,
    phraseClass: 'noselect stroketext mb-0 link--text text-center',
    hidePhrase: false,
    hidePhonetic: false,
    hideNative: false,
    abcTextVisible: true,
    coursesPersonalized: Array<PersonalizedCourse>(),
    personalizedCourse: null as PersonalizedCourse | null,
    lastVisitedPhraseForEmail: null as LastVisitedPhraseForSupport | null,
    rapidTestSID: '',
  },
  getters: {
    appComponentStyle(state): string {
      return `overflow-x: hidden; overflow-y: auto; max-width: 800px; max-height: ${state.windowHeight - 64 - 36 - 44 - 32}px;`;
    },
    isLandscape: (state): boolean => {
      if (state.breakpoint.xs) {
        return state.windowWidth > state.windowHeight * 1.1;
      }
      if (state.breakpoint.sm) {
        return state.windowWidth > state.windowHeight * 1.4;
      }
      return state.windowWidth > state.windowHeight * 1.7;
    },
    effectsVolume: (state) => state.globalVolume * 0.4,
    activityHeight: (state, getters): number => {
      let result;
      if (getters.isLandscape) {
        if (state.breakpoint.smAndDown) {
          result = state.windowHeight - 50 - 50 - 185;
        } else {
          result = state.windowHeight - 50 - 50 - 70 - 185;
        }
      } else if (state.breakpoint.xs) {
        result = state.windowHeight - 50 - 50 - 185;
      } else {
        result = state.windowHeight - 50 - 50 - 70 - 185;
      }
      if (result < 150) {
        result = 150;
      }
      if (result > 563) {
        result = 563;
      }
      return result;
    },
    cardStyle: (state) => {
      if (state.breakpoint.smAndDown) {
        return { 'min-width': `${state.windowWidth}px` };
      }
      return { 'min-width': '740px' };
    },
    cardStyleWide: (state) => {
      if (state.breakpoint.smAndDown) {
        return { 'min-width': `${state.windowWidth}px` };
      }
      return { 'min-width': '1000px' };
    },
    iLangRtl: (state) => {
      if (state.langI && state.rtlLangs.indexOf(state.langI.id) !== -1) {
        return true;
      }
      return false;
    },
    lLangRtl: (state) => {
      if (state.langL && state.rtlLangs.indexOf(state.langL.id) !== -1) {
        return true;
      }
      return false;
    },
    videosVisible: (state) => {
      if (!state.langL) return false;
      if (!state.level) return false;
      const videoLang = state.videoLanguages.get(state.langL.id);
      if (!videoLang) return false;
      return videoLang.find((item) => item === state.level!.obj_id);
    },
    isSignLang: () => false,
    isMediaLevel: () => (levelId: string) => false,
    isMusicLevel: () => (levelId: string) => false,
    isMovieLevel: () => (levelId: string) => false,
    isPlayLevel: () => (levelId: string) => false,
    isWotsLevel: () => (levelId: string) => false,
    username(state): string {
      if (!state.user) {
        return '';
      }
      if (state.user.username) {
        return state.user.username;
      }
      return state.user.email?.split('@')[0];
    },
    userHasAccessToPersonalizedAndReview(state, getters): boolean {
      if (!state.user) return false;
      return !state.user.isPersonalizedCourseRestrictedNoSubscription || getters.isPremiumUser;
    },
    /* used only on mobiles
    isFullAccessUser(state) {
      if (!state.user) return false;
      if (state.user.lifetimePremium) return true;
      if (!state.user.instituteUserPin) return false;
      if (state.validInstitutePin) return true;
      return false;
    },
    */
    isPremiumUser(state): boolean {
      if (!state.user) return false;
      if (state.user.lifetimePremium) return true;
      if ((state.user.subscription && !state.user.subscription.expired)
        || (state.user.instituteUserPin && state.validInstitutePin)) {
        return true;
      }
      return false;
    },
    isRapidTestAllowed(state, getters) {
      if (!state.user) return false;
      if (state.user.rapidTest && state.user.rapidTest > 0) return true;
      if (getters.lifetimePremium) return true;
      if (state.user.instituteUserPin && state.validInstitutePin) return true;
      return false;
    },
    findPersonalizedCourseById: (state, getters) => (id: string) => state.coursesPersonalized
      .find((course) => course.id.toString() === id) || null,
    getLessonLast: (state) => (isPersonalized = false): FirebaseLesson | null => {
      let lessonLast: FirebaseLesson | null = null;
      state.userLessons.forEach((lesson: FirebaseLesson) => {
        if (!lesson.isBookmark
          && (isPersonalized ? lesson.personalized !== '' : lesson.personalized === '')
          && (!lessonLast || lessonLast.lastAccessSeconds < lesson.lastAccessSeconds)) {
          lessonLast = lesson;
        }
      });
      return lessonLast;
    },
    lessonLast(state, getters): FirebaseLesson | null {
      return getters.getLessonLast(false);
    },
    lessonLastPersonalized(state, getters): FirebaseLesson | null {
      return getters.getLessonLast(true);
    },
  },
  mutations: {
    setWindowSize(state, payload) {
      state.windowWidth = payload.width;
      state.windowHeight = payload.height;
    },
    setSleepPromise(state, payload) {
      state.sleepPromise = payload;
    },
    setMainLoading(state, value: boolean) {
      state.mainLoading = value;
    },
    setOsName(state, value) {
      state.osName = value;
    },
    setBreakpoint(state, bp) {
      state.breakpoint = bp;
    },
    setAlertMessage(state, value: AlertMessage | null) {
      if (value) {
        state.alertMessageText = value.text;
        if (value.error) {
          console.log(value.error);
        }
      } else {
        state.alertMessageText = '';
      }
    },
    setUsernamePopup(state, value: boolean) {
      state.usernamePopup = value;
    },
    setMobileLinksPopup(state, value: boolean) {
      state.mobileLinksPopup = value;
    },
    setDevelopmentPopup(state, value: boolean) {
      state.developmentPopup = value;
    },
    setTranslations(state, value) {
      value.Gender1 = value.GenderMasculine;
      value.Gender2 = value.GenderFeminine;
      value.Gender3 = value.GenderNeuter;
      value.Gender4 = value.GenderCommon;
      value.Gender5 = value.GenderMasculineAnimate;
      value.Gender6 = value.GenderMasculineInanimate;
      value.Gender7 = value.GenderMasculinePersonal;
      value.Gender8 = value.GenderNA;
      state.tr = value;
    },
    setTranslationsDefault(state, value) {
      state.translationsDefault = value;
    },
    setLanguagesBluebird(state, value) {
      state.languagesBluebird = value;
    },
    setVideoLanguages(state, value) {
      state.videoLanguages = value;
    },
    setLanguagesI(state, value) {
      state.langsI = value;
    },
    setLanguagesL(state, value) {
      state.langsL = value;
    },
    setLangI(state, value) {
      state.langI = value;
    },
    setLangL(state, value) {
      state.langL = value;
    },
    setSkills(state, value) {
      state.skills = value;
    },
    setFirebaseToken(state, value) {
      state.firebaseToken = value;
    },
    setUser(state, value) {
      state.user = value;
    },
    setUserLessons(state, value) {
      state.userLessons = value;
    },
    setValidInstitutePin(state, value) {
      state.validInstitutePin = value;
    },
    setLevelsAllowed(state, value) {
      state.levelsAllowed = value;
    },
    setPostcardsImages(state, value) {
      state.postcardsImages = value;
    },
    setCoursesPersonalized(state, value) {
      state.coursesPersonalized = value;
    },
    setPersonalizedCourse(state, value) {
      state.personalizedCourse = value;
    },
    setLevels(state, value) {
      state.levels = value;
    },
    setLevelsPersonalized(state, value) {
      state.levelsPersonalized = value;
    },
    setLevelDaily(state, value) {
      state.levelDaily = value;
    },
    setLevel(state, value) {
      state.level = value;
    },
    setUnits(state, value) {
      state.units = value;
    },
    setUnit(state, value) {
      state.unit = value;
    },
    setLessons(state, value) {
      state.lessons = value;
    },
    setLesson(state, value) {
      state.lesson = value;
    },
    setPhrases(state, value) {
      state.phrases = value;
    },
    hidePhrase(state) {
      state.hidePhrase = !state.hidePhrase;
    },
    hidePhonetic(state) {
      state.hidePhonetic = !state.hidePhonetic;
    },
    hideNative(state) {
      state.hideNative = !state.hideNative;
    },
    setLastVisitedPhraseForEmail(state, value) {
      state.lastVisitedPhraseForEmail = value;
    },
    setRapidTestSID(state, value) {
      state.rapidTestSID = value;
    },
  },
  actions: {
  },
  modules: {
    api,
  },
});
